import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "../common/header";
import Footer from "../common/footer";
import { formatDateTime, getStatusText } from "../../lib/utils";
import { BookingDetailsData } from "../../types/booking";
import {
  Send,
  FileClock,
  FileCheck,
  CheckCircle2,
  Loader2,
  FileText,
  Search,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "react-router-dom";

const bookingSchema = z.object({
  bookingCode: z.string().min(1, "Please enter your booking code."),
});

const OrderTracking: React.FC = () => {
  const { id } = useParams(); // 👈 receive optional booking code from URL
  const [order, setOrder] = useState<BookingDetailsData | undefined>();
  const [error, setError] = useState("");
  const [isTracking, setIsTracking] = useState(false);

  const form = useForm<z.infer<typeof bookingSchema>>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      bookingCode: id || "", // 👈 pre-fill if id exists
    },
  });

  const handleTrackApplication = async (
    values: z.infer<typeof bookingSchema>
  ) => {
    setError("");
    setIsTracking(true);

    try {
      const response = await axios.get(
        `/booking/TrackOrder/${values.bookingCode}`
      );
      setOrder(response.data);
    } catch {
      setError("Booking not found or there was an error fetching the booking.");
      setOrder(undefined);
    } finally {
      setIsTracking(false);
    }
  };

  // ✅ Automatically track booking if ID exists in URL
  useEffect(() => {
    if (id) {
      handleTrackApplication({ bookingCode: id });
    }
  }, [id]);

  const getSteps = (order: BookingDetailsData) => {
    const status = order.bookingStatus ?? 0;

    const steps = [
      {
        icon: Send,
        name: "Pending",
        status: status === 0 ? "current" : status > 0 ? "completed" : "upcoming",
        date: formatDateTime(order.createDate?.toString()),
      },
      {
        icon: FileClock,
        name: "In Process",
        status: status === 1 ? "current" : status > 1 ? "completed" : "upcoming",
        date: formatDateTime(order.updateDate?.toString()),
      },
      {
        icon: FileCheck,
        name: "Canceled",
        status: status === 2 ? "current" : "upcoming",
        date:
          status === 2 ? formatDateTime(order.updateDate?.toString()) : null,
      },
      {
        icon: CheckCircle2,
        name: "Completed",
        status:
          status === 3 ? "current" : status < 3 ? "upcoming" : "completed",
        date:
          status === 3 ? formatDateTime(order.updateDate?.toString()) : null,
      },
    ];

    return steps;
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      <main className="flex-grow flex items-center justify-center mt-20 p-4 pt-24 md:pt-40">
        <div className="w-full max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-neutral-800">
              Track Your Application
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
              Enter your booking code below to see the real-time status of your
              visa application.
            </p>
          </div>

          {/* ✅ Booking Code Form */}
          <div className="space-y-8 max-w-2xl mx-auto">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleTrackApplication)}
                className="bg-white rounded-xl shadow-md p-6 space-y-4"
              >
                <FormField
                  control={form.control}
                  name="bookingCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Booking Code</FormLabel>
                      <FormControl>
                        <div className="flex items-center w-full rounded-md border border-input px-3 ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                          <FileText className="h-5 w-5 text-muted-foreground mr-3" />
                          <Input
                            placeholder="Enter your booking code"
                            {...field}
                            className="flex-1 border-0 p-0 bg-transparent placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0"
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={isTracking}
                  className="w-full bg-primary text-white py-3 rounded-lg flex items-center justify-center gap-2 text-lg hover:bg-primary/90 transition"
                >
                  {isTracking ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Tracking...
                    </>
                  ) : (
                    <>
                      <Search className="h-5 w-5" />
                      Track Booking
                    </>
                  )}
                </Button>

                {error && (
                  <p className="text-red-600 text-center mt-2">{error}</p>
                )}
              </form>
            </Form>

            {/* ✅ Booking Status Display */}
            {order && (
              <Card className="w-full max-w-2xl mx-auto shadow-lg border-gray-200 bg-white rounded-xl mt-10">
                <CardHeader>
                  <CardTitle>Application Status</CardTitle>
                  <CardDescription>
                    Reference # {order.bookingCode}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    <ul className="space-y-8">
                      {getSteps(order).map((step, index) => (
                        <li
                          key={index}
                          className={`flex items-start gap-6 ${
                            step.status === "upcoming" ? "opacity-50" : ""
                          }`}
                        >
                          <div
                            className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full ring-4 ring-background ${
                              step.status === "completed"
                                ? "bg-green-100 text-green-600"
                                : step.status === "current"
                                ? "bg-primary/10 text-primary animate-pulse"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            <step.icon className="w-6 h-6" />
                          </div>
                          <div className="flex-1 pt-2">
                            <h3 className="font-semibold text-lg">
                              {step.name}
                            </h3>
                            {step.date && (
                              <p className="text-sm text-gray-500">
                                {step.date}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Separator className="my-6" />

                  <div className="text-center">
                    <p className="text-gray-600">
                      Your booking details are displayed below.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Last updated:{" "}
                      {formatDateTime(order.updateDate?.toString())}
                    </p>
                  </div>

                  <Separator className="my-6" />

                  <div className="text-sm text-gray-700 space-y-1">
                    <p>
                      <strong>Customer Name:</strong> {order.customerName}
                    </p>
                    <p>
                      <strong>Service:</strong> {order.serviceName}{" "}
                      {order.duration}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      {getStatusText(order.bookingStatus!)}
                    </p>
                    <p>
                      <strong>Created:</strong>{" "}
                      {formatDateTime(order.createDate?.toString())}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OrderTracking;
